import type { TFunction } from 'i18next';

/** Shown in the draft message; replaced with the real URL when the organizer copies. */
export const INVITE_LINK_PLACEHOLDER = '<link>';

/**
 * The organizer does not send a bare URL — they send a message they can paste
 * into a chat. The draft includes `<link>` where the URL goes; they can move
 * that token anywhere in the text before copying.
 */

interface InviteMessageParts {
  hi: string;
  join: string;
  draw: string;
  openLink: string;
  seeYou: string;
  secret: string;
}

function assemble(parts: InviteMessageParts): string {
  return [
    parts.hi,
    '',
    parts.join,
    '',
    parts.draw,
    '',
    parts.openLink,
    INVITE_LINK_PLACEHOLDER,
    '',
    parts.seeYou,
    parts.secret,
  ].join('\n');
}

export interface InviteMessageInput {
  boxName: string;
  /** Whether the draw has already run — it changes what the invitee will see. */
  isActiveDraw: boolean;
}

/** Message for one person, addressed by name, that carries their own link. */
export function buildPersonalInviteMessage(
  input: InviteMessageInput & { username: string },
  t: TFunction,
): string {
  return assemble({
    hi: t('box.invite.personal.hi', { name: input.username }),
    join: t('box.invite.personal.join', { boxName: input.boxName }),
    draw: input.isActiveDraw
      ? t('box.invite.personal.drawDone')
      : t('box.invite.personal.drawSoon'),
    openLink: t('box.invite.personal.openLink'),
    seeYou: t('box.invite.seeYou'),
    secret: t('box.invite.secret'),
  });
}

/** Message for the group chat, where everyone picks their own name. */
export function buildSharedInviteMessage(input: InviteMessageInput, t: TFunction): string {
  return assemble({
    hi: t('box.invite.shared.hi'),
    join: t('box.invite.shared.join', { boxName: input.boxName }),
    draw: input.isActiveDraw ? t('box.invite.shared.drawDone') : t('box.invite.shared.drawSoon'),
    openLink: t('box.invite.shared.openLink'),
    seeYou: t('box.invite.seeYou'),
    secret: t('box.invite.secret'),
  });
}

function insertAfterPrompt(text: string, url: string, prompts: string[]): string {
  const lines = text.split('\n');
  const wanted = new Set(prompts.map((prompt) => prompt.trim()).filter(Boolean));
  const at = lines.findIndex((line) => wanted.has(line.trim()));
  if (at < 0) {
    return `${text}\n\n${url}`;
  }
  const after = lines.slice(at + 1);
  if (after[0]?.trim() === url) {
    return text;
  }
  // Drop the blank line that followed the prompt: the URL takes its place and
  // brings its own blank line after it.
  const rest = after[0]?.trim() === '' ? after.slice(1) : after;
  return [...lines.slice(0, at + 1), url, '', ...rest].join('\n');
}

/**
 * The text as it goes to the clipboard. Replaces every `<link>` with the URL.
 * Older drafts without the token still work: the URL is inserted after the
 * prompt line, or appended at the end.
 */
export function composeInviteClipboard(
  message: string,
  link: string,
  prompts: string[] = [],
): string {
  const text = message.trim();
  const url = link.trim();
  if (!url) {
    return text;
  }
  if (!text) {
    return url;
  }
  if (text.includes(url)) {
    return text;
  }
  if (text.includes(INVITE_LINK_PLACEHOLDER)) {
    return text.split(INVITE_LINK_PLACEHOLDER).join(url);
  }
  if (prompts.length > 0) {
    return insertAfterPrompt(text, url, prompts);
  }
  return `${text}\n\n${url}`;
}
